/**
 * MapBridge — frame-side implementation of the 3D-Map Bridge Contract v0.5
 * (Content post 4263, product section p-54b58ee81d07).
 *
 * The presenter runs inside a host iframe (?embed=1) as a pure renderer. The host owns
 * ALL domain data (pins, routes, user position) and pushes it via postMessage; the frame
 * never fetches domain data itself. Audio never crosses the boundary.
 *
 * Transport: window.postMessage, JSON { v: 1, type: string, ...payload }.
 * Unknown types are ignored (forward compatibility).
 */
import { Logger } from '../engine/core/Logger';

export interface BridgeGeoref {
    originLat: number;
    originLng: number;
    metersPerUnit: number;
    rotationDeg: number;
    originModel: { x: number; y: number; z: number };
    /** True when the terrain model uses +Z = north (default false = -Z north). Determined
     *  during calibration — the Tscheppa GLB uses +Z = north. */
    zNorthPositive?: boolean;
}

export interface BridgePin {
    id: number;
    kind: 'poi' | 'species' | 'story';
    lat?: number;
    lng?: number;
    model?: { x: number; y: number; z: number };
    /** Explicit WPT-node assignment (adjudicated table, post-4263 review). When present the
     *  frame places the pin on that GLB node directly; any wptNode in a push switches the
     *  whole push to explicit mode — the heuristic distance-snap is then disabled. */
    wptNode?: number;
    title: string;
    /** Optional rich content. The iframe host may keep rendering its own sheet; standalone
     *  app=map uses these fields for its local detail sheet. */
    description?: string;
    category?: string;
    subcategory?: string;
    color?: string;
    thumbnailUrl?: string;
    iconUrl?: string;
    balloonScale?: number;
    media?: BridgePinMedia[];
}

export interface BridgePinMedia {
    id: number;
    role: string;
    mimeType: string;
    url: string;
    thumbnailUrl?: string;
}

export interface BridgeRoute {
    id: number | string;
    color?: string;
    points: { lat: number; lng: number; ele?: number }[];
}

export interface BridgeMarker {
    lat: number;
    lng: number;
    style: 'start' | 'ziel' | 'info' | 'kassa';
    title?: string;
    glyph?: string;
}

/** v0.5: one region-level AR track as a distance-LOD unit. */
export interface BridgeTrack {
    trackId: number;
    name: string;
    lat: number;
    lng: number;
    detailMeter: number;
    silhouetteMeter: number;
    silhouette?: { lat: number; lng: number }[][];
}

export interface BridgeDelegate {
    onInit(payload: { lang?: string; theme?: string; georef?: BridgeGeoref }): void;
    onSetPins(pins: BridgePin[]): void;
    onSetRoutes(routes: BridgeRoute[], markers?: { start?: BridgeMarker; ziel?: BridgeMarker; extra?: BridgeMarker[] }): void;
    onSelectRoute(routeId: number | string | null): void;
    onSetUserPosition(pos: { lat: number; lng: number; heading?: number; accuracy?: number } | null): void;
    onFocusPoi(id: number, zoom?: number, distanceMeters?: number): void;
    onSetActivePoi(id: number | null): void;
    onFlyTo(payload: { lat: number; lng: number; distance?: number; distanceMeters?: number; durationMs?: number }): void;
    /** v0.3: relative camera zoom; factor > 1 zooms IN (distance /= factor), clamped by the frame. */
    onZoomBy(factor: number): void;
    /** v0.3: show only pins whose category is listed; null = show all. */
    onSetCategoryFilter(categories: string[] | null): void;
    /** v0.4.1: toggle the optional waterways overlay on capable renderers. */
    onSetWaterwaysVisible(visible: boolean): void;
    /** v0.5: full-replace track register (supported renderer instances only). */
    onSetTracks(tracks: BridgeTrack[]): void;
}

const PROTOCOL_VERSION = 1;
const BASE_CAPABILITIES = ['pins', 'routes', 'focusPoi', 'activePoi', 'flyTo', 'zoom', 'distanceMeters', 'categoryFilter'];

export interface MapBridgeOptions {
    /** Capabilities implemented only by this concrete renderer instance. */
    additionalCapabilities?: string[];
}

// Host origins allowed to drive this frame (contract §Transport) + local dev.
const ALLOWED_ORIGIN_PATTERNS: RegExp[] = [
    /^https:\/\/tschepp\.arkserver\.arkturian\.com$/,
    // Terrain-Demo-/Testdeployment (bridge-test-terrain.html, Kaernten-Slug)
    /^https:\/\/kaernten\.arkserver\.arkturian\.com$/,
    /^https:\/\/tscheppaschlucht\.arkturian\.com$/,
    /^http:\/\/localhost(:\d+)?$/,
    /^http:\/\/127\.0\.0\.1(:\d+)?$/,
];

export class MapBridge {
    private delegate: BridgeDelegate;
    private readonly capabilities: string[];
    private hostOrigin: string | null = null;
    private started = false;
    private lastCameraSend = 0;
    private static readonly CAMERA_THROTTLE_MS = 250;

    constructor(delegate: BridgeDelegate, options: MapBridgeOptions = {}) {
        this.delegate = delegate;
        this.capabilities = [...new Set([
            ...BASE_CAPABILITIES,
            ...(options.additionalCapabilities ?? []),
        ])];
    }

    /** True when the page runs in embed mode inside an allowed host. */
    public static isEmbedActive(): boolean {
        try {
            return new URLSearchParams(window.location.search).get('embed') === '1' && window.parent !== window;
        } catch { return false; }
    }

    public start(): void {
        if (this.started) return;
        this.started = true;
        window.addEventListener('message', this.onMessage);
        this.sendReady();
        Logger.Instance.Info(`MapBridge started — ready sent (caps: ${this.capabilities.join(',')})`, 'Bridge');
    }

    public stop(): void {
        window.removeEventListener('message', this.onMessage);
        this.started = false;
    }

    // ---------------- frame → host ----------------

    private post(msg: Record<string, any>): void {
        try {
            // Until the host has spoken (and proven its origin), target '*' is unavoidable for
            // 'ready'. The payload carries no secrets; all sensitive direction is host→frame.
            window.parent.postMessage({ v: PROTOCOL_VERSION, ...msg }, this.hostOrigin ?? '*');
        } catch { /* parent gone */ }
    }

    public sendReady(): void {
        this.post({ type: 'ready', caps: this.capabilities });
    }

    public sendPoiClicked(id: number): void {
        this.post({ type: 'poiClicked', id });
    }

    public sendPoiHover(id: number | null): void {
        this.post({ type: 'poiHover', id });
    }

    public sendCameraChanged(data: { lat: number; lng: number; distance: number; heading: number; pitch: number }): void {
        const now = performance.now();
        if (now - this.lastCameraSend < MapBridge.CAMERA_THROTTLE_MS) return;
        this.lastCameraSend = now;
        this.post({ type: 'cameraChanged', ...data });
    }

    public sendError(code: string, message: string): void {
        this.post({ type: 'error', code, message });
        Logger.Instance.Warn(`Bridge error sent: ${code} — ${message}`, 'Bridge');
    }

    public sendTrackDetailRequested(trackId: number, requestId: number): void {
        this.post({ type: 'trackDetailRequested', trackId, requestId });
    }

    public sendTrackDetailReleased(trackId: number, requestId: number): void {
        this.post({ type: 'trackDetailReleased', trackId, requestId });
    }

    // ---------------- host → frame ----------------

    private onMessage = (e: MessageEvent): void => {
        if (!ALLOWED_ORIGIN_PATTERNS.some(rx => rx.test(e.origin))) return;
        const msg = e.data;
        if (!msg || typeof msg !== 'object' || typeof msg.type !== 'string') return;

        // Lock onto the first allowed origin that speaks — all our replies target it.
        if (!this.hostOrigin) this.hostOrigin = e.origin;

        try {
            switch (msg.type) {
                case 'init':
                    this.delegate.onInit({ lang: msg.lang, theme: msg.theme, georef: msg.georef });
                    break;
                case 'setPins':
                    this.delegate.onSetPins(Array.isArray(msg.pins) ? msg.pins : []);
                    break;
                case 'setRoutes':
                    this.delegate.onSetRoutes(Array.isArray(msg.routes) ? msg.routes : [], msg.markers);
                    break;
                case 'selectRoute':
                    this.delegate.onSelectRoute(msg.routeId ?? null);
                    break;
                case 'setUserPosition':
                    this.delegate.onSetUserPosition(msg.lat !== undefined && msg.lat !== null ? { lat: msg.lat, lng: msg.lng, heading: msg.heading, accuracy: msg.accuracy } : null);
                    break;
                case 'focusPoi':
                    this.delegate.onFocusPoi(msg.id, msg.zoom, msg.distanceMeters);
                    break;
                case 'setActivePoi':
                    this.delegate.onSetActivePoi(msg.id ?? null);
                    break;
                case 'flyTo':
                    this.delegate.onFlyTo({ lat: msg.lat, lng: msg.lng, distance: msg.distance, distanceMeters: msg.distanceMeters, durationMs: msg.durationMs });
                    break;
                case 'zoomBy':
                    if (typeof msg.factor === 'number' && isFinite(msg.factor) && msg.factor > 0) {
                        this.delegate.onZoomBy(msg.factor);
                    } else {
                        this.sendError('BAD_PAYLOAD', `zoomBy factor=${msg.factor}`);
                    }
                    break;
                case 'setCategoryFilter':
                    this.delegate.onSetCategoryFilter(Array.isArray(msg.categories) ? msg.categories : null);
                    break;
                case 'setWaterwaysVisible':
                    if (typeof msg.visible === 'boolean') {
                        if (this.capabilities.includes('waterways')) {
                            this.delegate.onSetWaterwaysVisible(msg.visible);
                        }
                    } else {
                        this.sendError('BAD_PAYLOAD', `setWaterwaysVisible visible=${JSON.stringify(msg.visible)}`);
                    }
                    break;
                case 'setTracks':
                    if (this.capabilities.includes('tracks')) {
                        if (Array.isArray(msg.tracks)) {
                            this.delegate.onSetTracks(msg.tracks);
                        } else {
                            this.sendError('BAD_PAYLOAD', 'setTracks tracks must be an array');
                        }
                    }
                    break;
                case 'setPoiKenBurns':
                    // Phase ③ — visible no-op per contract review point (warn, don't silently drop)
                    Logger.Instance.Warn('setPoiKenBurns not implemented yet (phase 3) — no-op', 'Bridge');
                    break;
                default:
                    // Unknown types ignored (forward compat)
                    break;
            }
        } catch (err) {
            this.sendError('HANDLER_ERROR', `${msg.type}: ${err instanceof Error ? err.message : String(err)}`);
        }
    };
}
